import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CustomerDialog } from "./CustomerDialog";
import { CustomerDetailsDialog } from "./CustomerDetailsDialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileCustomerCard } from "./mobile/MobileCustomerCard";

interface Customer {
  id: string;
  name: string;
  phone: string;
  whatsapp?: string;
  description?: string;
  transaction_count?: number;
  quotation_count?: number;
}

export function CustomerTable() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data: customersData, error: customersError } = await supabase
        .from("customers")
        .select("*")
        .order("created_at", { ascending: false });

      if (customersError) throw customersError;

      const customersWithCounts = await Promise.all(
        customersData.map(async (customer) => {
          const { count: transactionCount, error: transactionError } = await supabase
            .from("transactions")
            .select("*", { count: "exact", head: true })
            .eq("customer_id", customer.id);

          if (transactionError) {
            console.error("Error fetching transaction count:", transactionError);
          }

          const { count: quotationCount, error: quotationError } = await supabase
            .from("quotations")
            .select("*", { count: "exact", head: true })
            .eq("customer_id", customer.id);

          if (quotationError) {
            console.error("Error fetching quotation count:", quotationError);
          }

          return {
            ...customer,
            transaction_count: transactionCount || 0,
            quotation_count: quotationCount || 0,
          };
        })
      );

      return customersWithCounts;
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel('public:customers')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customers'
        },
        (payload) => {
          console.log('Real-time update received:', payload);
          queryClient.invalidateQueries({ queryKey: ['customers'] });
          
          const operation = payload.eventType === 'INSERT' 
            ? 'adicionado' 
            : payload.eventType === 'UPDATE' 
              ? 'atualizado' 
              : 'removido';
          
          toast({
            title: `Cliente ${operation}`,
            description: `Um cliente foi ${operation} com sucesso!`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, toast]);

  const handleDelete = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedCustomer) return;

    try {
      const { error } = await supabase
        .from("customers")
        .delete()
        .eq("id", selectedCustomer.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Cliente excluído com sucesso!",
      });
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o cliente.",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
    }
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailsDialogOpen(true);
  };

  const handleSaveEdit = async (updatedCustomer: Omit<Customer, "id">) => {
    if (!selectedCustomer) return;

    try {
      const { error } = await supabase
        .from("customers")
        .update(updatedCustomer)
        .eq("id", selectedCustomer.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Cliente atualizado com sucesso!",
      });
    } catch (error) {
      console.error("Error updating customer:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o cliente.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#0073B7] border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-2">Carregando clientes...</p>
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <div className="p-2 space-y-3">
          {customers?.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              Nenhum cliente encontrado
            </div>
          ) : (
            customers?.map((customer) => (
              <MobileCustomerCard
                key={customer.id}
                customer={customer}
                onViewDetails={handleViewDetails}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir este cliente? Esta ação não pode ser
                desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>
                Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {selectedCustomer && (
          <>
            <CustomerDialog
              isOpen={editDialogOpen}
              onOpenChange={setEditDialogOpen}
              onSave={handleSaveEdit}
              initialData={selectedCustomer}
            />
            <CustomerDetailsDialog
              customerId={selectedCustomer.id}
              customerName={selectedCustomer.name}
              isOpen={detailsDialogOpen}
              onOpenChange={setDetailsDialogOpen}
            />
          </>
        )}
      </>
    );
  }

  return (
    <>
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Transações</TableHead>
              <TableHead>Orçamentos</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers?.map((customer) => (
              <TableRow
                key={customer.id}
                className="cursor-pointer hover:bg-accent/50"
                onClick={() => handleViewDetails(customer)}
              >
                <TableCell className="font-medium">{customer.name}</TableCell>
                <TableCell>{customer.phone}</TableCell>
                <TableCell>{customer.whatsapp || "-"}</TableCell>
                <TableCell>{customer.description || "-"}</TableCell>
                <TableCell>{customer.transaction_count}</TableCell>
                <TableCell>{customer.quotation_count}</TableCell>
                <TableCell className="text-right">
                  <div
                    className="flex justify-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleViewDetails(customer)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(customer)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(customer)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este cliente? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedCustomer && (
        <>
          <CustomerDialog
            isOpen={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            onSave={handleSaveEdit}
            initialData={selectedCustomer}
          />
          <CustomerDetailsDialog
            customerId={selectedCustomer.id}
            customerName={selectedCustomer.name}
            isOpen={detailsDialogOpen}
            onOpenChange={setDetailsDialogOpen}
          />
        </>
      )}
    </>
  );
}
